import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { fetchGeoDirectory } from "../api/geoApi";
import { createGeoDirectoryStore, type GeoDirectoryStore } from "../data/geoDirectoryStore";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";

type GeoDirectoryContextValue = {
  store: GeoDirectoryStore | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

const GeoDirectoryContext = createContext<GeoDirectoryContextValue | null>(null);

export function GeoDirectoryProvider({ children }: { children: ReactNode }) {
  const { showToast } = useToast();
  const { can, loading: authLoading } = useAuth();
  const [store, setStore] = useState<GeoDirectoryStore | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canGeo = can("geo_directory") || can("geo_directory_admin");
  /** Leads import matches mandals/villages; users may have leads rights without geo_directory — `fetchGeoDirectory` falls back to bundled JSON when the API is forbidden or unavailable. */
  const needsDirectoryForLeads = can("leads") || can("leads_import");

  const load = useCallback(async () => {
    setError(null);
    if (authLoading) {
      return;
    }
    setLoading(true);
    if (!canGeo && !needsDirectoryForLeads) {
      setStore(null);
      setLoading(false);
      return;
    }
    try {
      const data = await fetchGeoDirectory();
      setStore(createGeoDirectoryStore(data));
    } catch (e) {
      setStore(null);
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  }, [showToast, authLoading, canGeo, needsDirectoryForLeads]);

  useEffect(() => {
    void load();
  }, [load]);

  const value = useMemo(
    () => ({ store, loading, error, refetch: load }),
    [store, loading, error, load],
  );

  return <GeoDirectoryContext.Provider value={value}>{children}</GeoDirectoryContext.Provider>;
}

export function useGeoDirectory(): GeoDirectoryContextValue {
  const ctx = useContext(GeoDirectoryContext);
  if (!ctx) {
    throw new Error("useGeoDirectory must be used within GeoDirectoryProvider");
  }
  return ctx;
}
